import { Table, Typography, useRect } from '@statsbomb/kitbag-components';
import { FilterSetType } from '@statsbomb/parachute-types';
import { useSetAtom } from 'jotai';
import { ScopeProvider } from 'jotai-scope';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { twJoin } from 'tailwind-merge';
import { userConfigActionAtom, userConfigIdAtom, userConfigNameAtom, userConfigTypeAtom } from '@/atoms/userConfigs';
import { GenericToast } from '@/components/alerts/GenericToast/GenericToast';
import { UserConfigMenu } from '@/components/menu/UserConfigMenu/UserConfigMenu';
import { GenericDeleteModal } from '@/components/modal/GenericDeleteModal/GenericDeleteModal';
import { GenericRenameModal } from '@/components/modal/GenericRenameModal/GenericRenameModal';
import { SaveFilterModal } from '@/components/modal/SaveFilterModal/SaveFilterModal';
import { TableWrapper } from '@/components/tables/TableWrapper';
import { userConfigActionAtoms } from '@/consts/scope';
import { SYSTEM_WIDE_USER_ID } from '@/consts/userConfigs';
import { useFilterSets } from '@/hooks/useFilterSets';
import { useFilterSetType } from '@/hooks/useFilterSetType';
import { useFormatDate } from '@/hooks/useFormatDate';
import { useIsTouchDevice } from '@/hooks/useIsTouchDevice';
import { useSortObjectArray } from '@/hooks/useSortObjectArray';
import { useSortTable } from '@/hooks/useSortTable';
import { UserConfigActions } from '@/types/userConfigs';
import { getFilterCount } from '@/utils/filters';
import { TableScopeProvider } from '../TableScopeProvider';
import { FilterSetTableStatus } from './FilterSetTableStatus';

type FilterSetTableProps = {
  tableWrapperClasses?: string;
  selectedFilterIdToLoad?: string | null;
  onClick?: (value: string) => void;
  filterSets: ReturnType<typeof useFilterSets>;
};

const CELL_X_PADDING = 16;
const OPTIONS_BASE_WIDTH = 32;
const OPTIONS_WIDTH = OPTIONS_BASE_WIDTH + CELL_X_PADDING;
const OWNER_BASE_WIDTH = 120;
const OWNER_WIDTH = OWNER_BASE_WIDTH + CELL_X_PADDING;
const LAST_MODIFIED_BASE_WIDTH = 90;
const SCROLL_BAR = 16;

const ScopedFilterSetTable = ({
  tableWrapperClasses,
  selectedFilterIdToLoad,
  onClick,
  filterSets,
}: FilterSetTableProps) => {
  const { t } = useTranslation(['filters', 'general', 'table', 'dataLocker', 'entity']);
  const formatDate = useFormatDate();
  const { orderBy, orderDirection, handleSort, getSortedState } = useSortTable();

  const setUserConfigName = useSetAtom(userConfigNameAtom);
  const setUserConfigToAmend = useSetAtom(userConfigIdAtom);
  const setUserConfigTypeToAmend = useSetAtom(userConfigTypeAtom);
  const setUserConfigActions = useSetAtom(userConfigActionAtom);

  useEffect(() => handleSort('updatedAt'), []);
  const sortedFilterSets = useSortObjectArray(
    orderBy,
    filterSets.map(filterSet => ({
      ...filterSet,
      renderedName: filterSet.definition.titleSettings.title || '',
    })),
    orderDirection,
  );

  const [ref, rect] = useRect<HTMLTableElement>();

  const isTouch = useIsTouchDevice();
  const theme = useTheme();
  const isLarge = theme.mqMatch('lg');

  const lastModifiedWidth = isTouch ? 0 : LAST_MODIFIED_BASE_WIDTH + CELL_X_PADDING;
  const filtersCountWidth = isLarge ? 0 : 42;
  const gap = isLarge ? 8 : 16;

  // Truncating text dynamically in tables only with CSS is impossible so to achieve this, we calculate the table's
  // width and derive the "set width" the dynamic column should be which enables us to truncate the text.
  const nameCellWidth =
    rect.width -
    OWNER_WIDTH -
    OPTIONS_WIDTH -
    lastModifiedWidth -
    filtersCountWidth -
    gap -
    CELL_X_PADDING -
    SCROLL_BAR;

  const hasData = sortedFilterSets.length > 0;

  return (
    <>
      {hasData && (
        <TableWrapper classes={twJoin('z-0', tableWrapperClasses)}>
          <div className="overflow-auto h-full" ref={ref}>
            <Table isHeadSticky withBorder={false}>
              <Table.Head>
                <Table.Row>
                  <Table.HeaderCell
                    sortCb={() => handleSort('renderedName', true)}
                    sortState={getSortedState('renderedName')}
                    size="regular"
                  >
                    {/* This is only to keep the text inline with the row values below due to the border they have */}
                    <span className="border-transparent border-l-4">{t('name', { ns: 'table' })}</span>
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    sortCb={() => handleSort('updatedAt', false)}
                    sortState={getSortedState('updatedAt')}
                    size="regular"
                    className="hidden md:table-cell"
                    style={{ maxWidth: LAST_MODIFIED_BASE_WIDTH }}
                  >
                    {t('lastModified', { ns: 'general' })}
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    sortCb={() => handleSort('ownerName', true)}
                    sortState={getSortedState('ownerName')}
                    size="regular"
                  >
                    {t('owner', { ns: 'general' })}
                  </Table.HeaderCell>
                  <Table.HeaderCell size="regular" />
                </Table.Row>
              </Table.Head>
              <Table.Body>
                {sortedFilterSets.map(filterSet => {
                  const id = filterSet.configId;
                  const isSelected = selectedFilterIdToLoad === id;

                  return (
                    <Table.Row
                      key={id}
                      onClick={() => onClick && onClick(id)}
                      className={onClick && 'cursor-pointer'}
                      data-testid="filter-set-row"
                    >
                      <Table.DataCell
                        alignVertically
                        className={twJoin(
                          'border-l-4',
                          isSelected ? 'border-l-interface-main' : 'border-l-transparent',
                        )}
                      >
                        <Typography
                          variant="bodyRegular"
                          as="span"
                          title={filterSet.renderedName}
                          style={{
                            maxWidth: nameCellWidth,
                          }}
                          className="truncate"
                        >
                          {filterSet.renderedName}
                        </Typography>
                        <Typography variant="bodySmall" as="span" className="hidden lg:block">
                          {t('filtersCount', {
                            count: getFilterCount(filterSet.definition.filters),
                          })}
                        </Typography>
                      </Table.DataCell>
                      <Table.DataCell className="hidden md:table-cell">
                        <Typography variant="bodyRegular" as="span">
                          {formatDate(filterSet.updatedAt)}
                        </Typography>
                      </Table.DataCell>
                      <Table.DataCell>
                        <Typography
                          variant="bodyRegular"
                          as="span"
                          className="block truncate"
                          style={{ maxWidth: OWNER_BASE_WIDTH }}
                        >
                          {filterSet.ownerName}
                        </Typography>
                      </Table.DataCell>
                      <Table.DataCell className="w-8">
                        {filterSet.hudlUserId !== SYSTEM_WIDE_USER_ID && (
                          <UserConfigMenu
                            configId={id}
                            onRenameClick={(name: string) => {
                              setUserConfigToAmend(id);
                              setUserConfigName(name);
                              setUserConfigTypeToAmend(FilterSetType.FILTERS);
                              setUserConfigActions(UserConfigActions.RENAME);
                            }}
                            onDeleteClick={(id: string, name: string) => {
                              setUserConfigToAmend(id);
                              setUserConfigName(name);
                              setUserConfigTypeToAmend(FilterSetType.FILTERS);
                              setUserConfigActions(UserConfigActions.DELETE);
                            }}
                          />
                        )}
                      </Table.DataCell>
                    </Table.Row>
                  );
                })}
              </Table.Body>
            </Table>
          </div>
        </TableWrapper>
      )}
      {!hasData && <FilterSetTableStatus />}
      {/* Have added this here as without this the Toast shows underneath the modal */}
      <GenericToast />
      <GenericRenameModal />
      <SaveFilterModal />
      <GenericDeleteModal />
    </>
  );
};

export const FilterSetTableBase = ({
  tableWrapperClasses,
  selectedFilterIdToLoad,
  onClick,
  filterSets,
}: FilterSetTableProps) => (
  <TableScopeProvider>
    <ScopeProvider atoms={userConfigActionAtoms}>
      <ScopedFilterSetTable
        tableWrapperClasses={tableWrapperClasses}
        selectedFilterIdToLoad={selectedFilterIdToLoad}
        onClick={onClick}
        filterSets={filterSets}
      />
    </ScopeProvider>
  </TableScopeProvider>
);

export const FilterSetTable = ({
  tableWrapperClasses,
  selectedFilterIdToLoad,
  onClick,
}: Omit<FilterSetTableProps, 'filterSets'>) => (
  <FilterSetTableBase
    tableWrapperClasses={tableWrapperClasses}
    selectedFilterIdToLoad={selectedFilterIdToLoad}
    onClick={onClick}
    filterSets={useFilterSets(useFilterSetType())}
  />
);
